import React, { useState, useEffect, useRef, useCallback } from "react";
import { FormControl, InputGroup, ListGroup, Spinner } from "react-bootstrap";
import SafetyLavoratoriService from "../../../services/safety/SafetyLavoratoriService";

const DEBOUNCE_MS = 300;
const RESULT_LIMIT = 30;

function formatLabel(lav) {
    if (!lav) return "";
    const nome = `${lav.cognome || ""} ${lav.nome || ""}`.trim();
    if (lav.codice_fiscale) return `${nome} · ${lav.codice_fiscale}`;
    if (lav.mansione_denominazione) return `${nome} · ${lav.mansione_denominazione}`;
    return nome || "—";
}

async function parseRows(resp) {
    const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
    return Array.isArray(data) ? data : (data?.rows || data?.data || []);
}

/**
 * Selettore lavoratore con ricerca server-side (adatto a centinaia di anagrafiche).
 * Usa POST /safety/lavoratori/find con filtri.q e limite paginato.
 */
export default function LavoratoreSearchPicker({
    idAzienda,
    value = "",
    onChange,
    allowEmpty = false,
    emptyLabel = "— Nessuno —",
    placeholder = "Cerca cognome, nome, CF, email…",
    disabled = false,
}) {
    const [searchTerm, setSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState("");
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const debounceRef = useRef(null);
    const requestIdRef = useRef(0);

    const valueStr = value !== null && value !== undefined && value !== "" ? String(value) : "";

    const fetchWorkers = useCallback(async (q = "") => {
        if (!idAzienda) {
            setOptions([]);
            return;
        }
        const reqId = ++requestIdRef.current;
        setLoading(true);
        try {
            const filtri = { id_azienda: Number(idAzienda), attivo: 1 };
            const term = (q || "").trim();
            if (term) filtri.q = term;
            const resp = await SafetyLavoratoriService.find({
                filtri,
                pagina: 0,
                limite: RESULT_LIMIT,
                ordinamento: "cognome",
            });
            if (reqId !== requestIdRef.current) return;
            setOptions(await parseRows(resp));
        } catch {
            if (reqId === requestIdRef.current) setOptions([]);
        } finally {
            if (reqId === requestIdRef.current) setLoading(false);
        }
    }, [idAzienda]);

    const loadSelected = useCallback(async (idLav) => {
        if (!idLav) {
            setSelectedLabel("");
            return;
        }
        try {
            const resp = await SafetyLavoratoriService.show(idLav);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setSelectedLabel(formatLabel(data));
        } catch {
            setSelectedLabel("");
        }
    }, []);

    useEffect(() => {
        if (valueStr) {
            loadSelected(valueStr);
        } else {
            setSelectedLabel("");
            if (!isOpen) setSearchTerm("");
        }
    }, [valueStr, loadSelected, isOpen]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
                if (valueStr) setSearchTerm("");
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [valueStr]);

    useEffect(() => () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
    }, []);

    const scheduleSearch = (term) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchWorkers(term), DEBOUNCE_MS);
    };

    const handleFocus = () => {
        if (disabled || !idAzienda) return;
        setIsOpen(true);
        setSearchTerm("");
        fetchWorkers("");
    };

    const handleInputChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setIsOpen(true);
        if (!term && allowEmpty) {
            onChange("");
            setSelectedLabel("");
        }
        scheduleSearch(term);
    };

    const handleSelect = (lav) => {
        if (!lav) {
            onChange("");
            setSelectedLabel("");
            setSearchTerm("");
        } else {
            onChange(String(lav.id_lavoratore));
            setSelectedLabel(formatLabel(lav));
            setSearchTerm("");
        }
        setIsOpen(false);
    };

    const handleClear = () => {
        onChange("");
        setSelectedLabel("");
        setSearchTerm("");
        setIsOpen(false);
        inputRef.current?.focus();
    };

    const displayValue = isOpen ? searchTerm : (valueStr ? selectedLabel : searchTerm);

    if (!idAzienda) {
        return (
            <FormControl size="sm" disabled placeholder="Seleziona prima l'azienda" />
        );
    }

    return (
        <div ref={containerRef} className="position-relative">
            <InputGroup size="sm">
                <FormControl
                    ref={inputRef}
                    size="sm"
                    placeholder={placeholder}
                    value={displayValue}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    disabled={disabled}
                    autoComplete="off"
                />
                {valueStr && !disabled && (
                    <InputGroup.Text role="button" onClick={handleClear} style={{ cursor: "pointer" }}>
                        <i className="bi bi-x-lg" />
                    </InputGroup.Text>
                )}
                <InputGroup.Text
                    role="button"
                    style={{ cursor: disabled ? "default" : "pointer" }}
                    onClick={() => !disabled && (isOpen ? setIsOpen(false) : handleFocus())}
                >
                    <i className={`bi bi-chevron-${isOpen ? "up" : "down"}`} />
                </InputGroup.Text>
            </InputGroup>

            {isOpen && !disabled && (
                <div
                    className="bg-white border rounded shadow-sm"
                    style={{
                        position: "absolute",
                        zIndex: 1050,
                        width: "100%",
                        maxHeight: "280px",
                        overflowY: "auto",
                        marginTop: "2px",
                    }}
                >
                    {loading ? (
                        <div className="text-center text-muted small p-3">
                            <Spinner size="sm" animation="border" className="me-2" />
                            Ricerca…
                        </div>
                    ) : (
                        <ListGroup variant="flush">
                            {allowEmpty && (
                                <ListGroup.Item
                                    action
                                    className="small py-2 text-muted"
                                    onClick={() => handleSelect(null)}
                                >
                                    {emptyLabel}
                                </ListGroup.Item>
                            )}
                            {options.length === 0 ? (
                                <ListGroup.Item className="small py-2 text-muted text-center">
                                    {searchTerm.trim()
                                        ? "Nessun lavoratore trovato"
                                        : "Digita per cercare oppure scorri l'elenco iniziale"}
                                </ListGroup.Item>
                            ) : (
                                options.map(lav => {
                                    const sel = valueStr === String(lav.id_lavoratore);
                                    return (
                                        <ListGroup.Item
                                            key={lav.id_lavoratore}
                                            action
                                            active={sel}
                                            className="small py-2"
                                            onClick={() => handleSelect(lav)}
                                        >
                                            {formatLabel(lav)}
                                        </ListGroup.Item>
                                    );
                                })
                            )}
                        </ListGroup>
                    )}
                    {!loading && options.length >= RESULT_LIMIT && (
                        <div className="small text-muted text-center py-1 border-top">
                            Mostrati i primi {RESULT_LIMIT} risultati — affina la ricerca
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
